import { useCallback } from 'react';

import {
  Autocomplete,
  CircularProgress,
  InputAdornment,
  TextField,
  createFilterOptions,
} from '@frontierag/frontier-ui-library';
import type { AutocompleteRenderInputParams } from '@frontierag/frontier-ui-library';
import {
  CancelOutlined as CancelOutlinedIcon,
  ExpandMore as ExpandMoreIcon,
  SearchOutlined as SearchOutlinedIcon,
} from '@frontierag/frontier-ui-library/icons';

import { styles } from './styles';
import type { IDynamicAutocomplete } from './types';

const DynamicAutocomplete = <T extends object>(props: IDynamicAutocomplete<T>) => {
  const {
    startAdornmentIcon = <SearchOutlinedIcon />,
    textFieldProps,
    displayPopupIcon = false,
    filterOptionsProps,
    ...autocompleteProps
  } = props;

  const filterOptions = createFilterOptions({
    matchFrom: 'any',
    ignoreCase: true,
    trim: true,
    stringify: (option: T) =>
      autocompleteProps.getOptionLabel
        ? autocompleteProps.getOptionLabel(option)
        : option.toString(),
    ...filterOptionsProps,
  });

  const renderInput = useCallback(
    (params: AutocompleteRenderInputParams) => {
      return (
        <TextField
          {...params}
          inputProps={{
            ...params.inputProps,
            autoComplete: 'off',
          }}
          InputLabelProps={{
            shrink: true,
          }}
          InputProps={{
            ...params.InputProps,
            autoComplete: 'off',

            startAdornment: <InputAdornment position="start">{startAdornmentIcon}</InputAdornment>,
            endAdornment: (
              <>
                {autocompleteProps.loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          variant="outlined"
          fullWidth
          {...textFieldProps}
        />
      );
    },
    [autocompleteProps.loading, textFieldProps, startAdornmentIcon],
  );

  return (
    <Autocomplete
      fullWidth
      disablePortal
      sx={styles}
      filterOptions={filterOptions}
      data-testid={`single-select-${textFieldProps.name}`}
      renderInput={renderInput}
      popupIcon={displayPopupIcon ? <ExpandMoreIcon /> : null}
      componentsProps={{
        popper: {
          sx: { pb: 1 },
        },
      }}
      clearIcon={<CancelOutlinedIcon />}
      {...autocompleteProps}
    />
  );
};

export default DynamicAutocomplete;
